import { useState, useEffect } from 'react';
// @ts-ignore
import { getServices, createService, updateService, deleteService } from '../../../services/serviceService';
// @ts-ignore
import uploadService from '../../../services/uploadService';

interface Service {
  _id: string;
  name: string;
  slug: string;
  subtitle?: string;
  description?: string;
  shortDescription?: string;
  icon?: string;
  featuredImage?: string;
  video?: string;
  content?: string;
  type: 'solution' | 'service' | 'academy';
  parentId?: Service | null;
  price?: string;
  features?: string[];
  order: number;
  isActive: boolean;
  isFeatured: boolean;
  createdAt: string;
  updatedAt: string;
}

const UnifiedServicesManagement = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [filterParent, setFilterParent] = useState<string>('all');
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [parentServices, setParentServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    subtitle: '',
    description: '',
    shortDescription: '',
    video: '',
    icon: 'ri-service-line',
    type: 'service' as 'solution' | 'service' | 'academy',
    parentId: null as string | null,
    features: [] as string[],
    order: 0,
    isActive: true,
    isFeatured: false
  });

  const [validationErrors, setValidationErrors] = useState({
    name: '',
    slug: '',
    shortDescription: '',
    icon: ''
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [imageError, setImageError] = useState<string>('');

  const typeOptions = [
    { value: 'solution', label: 'Giải pháp', icon: 'ri-lightbulb-line', color: 'bg-blue-100 text-blue-800' },
    { value: 'service', label: 'Dịch vụ', icon: 'ri-service-line', color: 'bg-green-100 text-green-800' },
    { value: 'academy', label: 'Academy', icon: 'ri-graduation-cap-line', color: 'bg-purple-100 text-purple-800' }
  ];

  // Fetch services từ API
  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getServices({ limit: 1000 });
      const allServices = response.data?.services || [];
      setServices(allServices as Service[]);
      setTotalItems(allServices.length);
      
      // Lọc ra các parent services (không có parentId)
      const parents = allServices.filter((s: any) => !s.parentId);
      setParentServices(parents as Service[]);
    } catch (error) {
      console.error('Error fetching services:', error);
      setError('Không thể tải dịch vụ. Vui lòng thử lại.');
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter services
  const filteredServices = services.filter(service => {
    const matchesSearch = service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         service.shortDescription?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === 'all' ? true : service.type === filterType;
    
    const matchesParent = filterParent === 'all' ? true :
                         filterParent === 'parent' ? !service.parentId :
                         service.parentId?._id === filterParent;
    
    return matchesSearch && matchesType && matchesParent;
  });

  // Calculate pagination
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedServices = filteredServices.slice(startIndex, endIndex);
  const calculatedTotalPages = Math.ceil(filteredServices.length / itemsPerPage);

  // Update total pages when filtered services change
  useEffect(() => {
    setTotalPages(calculatedTotalPages);
    // Reset to page 1 if current page is out of bounds
    if (currentPage > calculatedTotalPages && calculatedTotalPages > 0) {
      setCurrentPage(1);
    }
  }, [filteredServices.length, calculatedTotalPages, currentPage]);

  // Auto-generate slug from name
  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove accents
      .replace(/đ/g, 'd')
      .replace(/[^a-z0-9\s-]/g, '') // Remove special chars
      .trim()
      .replace(/\s+/g, '-') // Replace spaces with -
      .replace(/-+/g, '-'); // Replace multiple - with single -
  };

  // Validate form
  const validateForm = () => {
    const errors = {
      name: '',
      slug: '',
      shortDescription: '',
      icon: ''
    };

    let isValid = true;

    if (!formData.name.trim()) {
      errors.name = 'Tên là bắt buộc';
      isValid = false;
    } else if (formData.name.length < 3 || formData.name.length > 200) {
      errors.name = 'Tên phải có độ dài từ 3 đến 200 ký tự';
      isValid = false;
    }

    if (formData.slug && !/^[a-z0-9-]+$/.test(formData.slug)) {
      errors.slug = 'Slug chỉ được chứa chữ thường, số và dấu gạch ngang';
      isValid = false;
    }

    if (formData.shortDescription && formData.shortDescription.length > 200) {
      errors.shortDescription = 'Mô tả ngắn không được vượt quá 200 ký tự';
      isValid = false;
    }

    if (!formData.icon.trim()) {
      errors.icon = 'Icon là bắt buộc';
      isValid = false;
    } else if (!formData.icon.startsWith('ri-')) {
      errors.icon = 'Icon phải bắt đầu với "ri-" (RemixIcon)';
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  const handleEdit = (service: Service) => {
    setSelectedService(service);
    setFormData({
      name: service.name,
      slug: service.slug || '',
      subtitle: (service as any).subtitle || '',
      description: service.description || '',
      shortDescription: service.shortDescription || '',
      video: (service as any).video || '',
      icon: service.icon || 'ri-service-line',
      type: service.type,
      parentId: service.parentId?._id || null,
      features: service.features || [],
      order: service.order,
      isActive: service.isActive,
      isFeatured: service.isFeatured
    });
    setValidationErrors({ name: '', slug: '', shortDescription: '', icon: '' });
    // Set preview image if service has featuredImage
    if (service.featuredImage) {
      setPreviewImage(service.featuredImage);
    } else {
      setPreviewImage('');
    }
    setSelectedFile(null);
    setImageError('');
    setIsEditModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa mục này?')) {
      try {
        await deleteService(id);
        await fetchServices();
      } catch (error: any) {
        console.error('Error deleting service:', error);
        const errorMessage = error?.response?.data?.message || 'Có lỗi xảy ra khi xóa.';
        alert(errorMessage);
      }
    }
  };

  const handleToggleActive = async (id: string) => {
    try {
      const service = services.find(s => s._id === id);
      if (service) {
        await updateService(id, { isActive: !service.isActive });
        await fetchServices();
      }
    } catch (error) {
      console.error('Error toggling service status:', error);
      alert('Có lỗi xảy ra khi cập nhật trạng thái.');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setImageError('Vui lòng chọn file ảnh hợp lệ');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setImageError('Kích thước ảnh không được vượt quá 5MB');
        return;
      }

      // Clear error
      setImageError('');
      setSelectedFile(file);

      // Preview image
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadImage = async (serviceId: string) => {
    if (!selectedFile) return null;
    
    try {
      const response = await uploadService.uploadFile(selectedFile, serviceId, 'Service');
      return response.data?.url || response.url;
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      const serviceData: any = {
        ...formData,
        name: formData.name.trim(),
        slug: formData.slug.trim() || undefined,
        shortDescription: formData.shortDescription.trim(),
        icon: formData.icon.trim(),
        parentId: formData.parentId || null
      };
      
      const response = await createService(serviceData);
      const newService = response.data;

      // Upload image if selected
      if (selectedFile && newService?._id) {
        try {
          await handleUploadImage(newService._id);
        } catch (uploadError) {
          console.error('Error uploading image:', uploadError);
        }
      }

      await fetchServices();
      setIsAddModalOpen(false);
      resetForm();
    } catch (error: any) {
      console.error('Error creating service:', error);
      const errorMessage = error?.response?.data?.message || 'Có lỗi xảy ra khi tạo.';
      alert(errorMessage);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (selectedService) {
      try {
        const serviceData: any = {
          ...formData,
          name: formData.name.trim(),
          slug: formData.slug.trim() || undefined,
          shortDescription: formData.shortDescription.trim(),
          icon: formData.icon.trim(),
          parentId: formData.parentId || null
        };
        
        await updateService(selectedService._id, serviceData);

        // Upload image if selected
        if (selectedFile) {
          try {
            await handleUploadImage(selectedService._id);
          } catch (uploadError) {
            console.error('Error uploading image:', uploadError);
          }
        }

        await fetchServices();
        setIsEditModalOpen(false);
        setSelectedService(null);
        resetForm();
      } catch (error: any) {
        console.error('Error updating service:', error);
        const errorMessage = error?.response?.data?.message || 'Có lỗi xảy ra khi cập nhật.';
        alert(errorMessage);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      subtitle: '',
      description: '',
      shortDescription: '',
      video: '',
      icon: 'ri-service-line',
      type: 'service',
      parentId: null,
      features: [],
      order: 0,
      isActive: true,
      isFeatured: false
    });
    setValidationErrors({ name: '', slug: '', shortDescription: '', icon: '' });
    setSelectedFile(null);
    setPreviewImage('');
    setImageError('');
  };

  const getTypeInfo = (type: string) => {
    return typeOptions.find(t => t.value === type) || typeOptions[1];
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Quản lý Dịch vụ & Giải pháp</h2>
          <p className="text-gray-600">Quản lý tất cả dịch vụ, giải pháp và academy trên một nền tảng</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setIsAddModalOpen(true);
          }}
          className="bg-[#2A4B7C] hover:bg-[#2A4B7C]/90 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          Thêm mới
        </button>
      </div>

      {/* Search and Filter */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
            <input
              type="text"
              placeholder="Tìm kiếm..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
          >
            <option value="all">Tất cả loại</option>
            {typeOptions.map((type) => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
          <select
            value={filterParent}
            onChange={(e) => setFilterParent(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
          >
            <option value="all">Tất cả cấp độ</option>
            <option value="parent">Chỉ cha</option>
            {parentServices.map((parent) => (
              <option key={parent._id} value={parent._id}>
                Con của: {parent.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Services Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2A4B7C]"></div>
              <span className="text-gray-600">Đang tải...</span>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="text-red-500 mb-2">
                <i className="ri-error-warning-line text-3xl"></i>
              </div>
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={fetchServices}
                className="bg-[#2A4B7C] hover:bg-[#2A4B7C]/90 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
              >
                Thử lại
              </button>
            </div>
          </div>
        ) : paginatedServices.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="text-gray-400 mb-2">
                <i className="ri-inbox-line text-3xl"></i>
              </div>
              <p className="text-gray-600">
                {searchTerm ? 'Không tìm thấy kết quả nào' : 'Chưa có dữ liệu'}
              </p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900">STT</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900">Icon</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900">Tên</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900">Loại</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900">Cấp độ</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900">Trạng thái</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900">Nổi bật</th>
                  <th className="text-left py-4 px-6 font-semibold text-gray-900">Thao tác</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedServices.map((service) => {
                  const typeInfo = getTypeInfo(service.type);
                  return (
                    <tr key={service._id} className="hover:bg-gray-50">
                      <td className="py-4 px-6">
                        <span className="inline-flex items-center justify-center w-8 h-8 bg-[#2A4B7C] text-white rounded-full font-semibold text-sm">
                          {service.order}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <div className="w-10 h-10 flex items-center justify-center bg-[#2A4B7C] rounded-lg">
                          <i className={`${service.icon || 'ri-service-line'} text-lg text-white`}></i>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <h3 className="font-medium text-gray-900">{service.name}</h3>
                        {service.shortDescription && (
                          <p className="text-sm text-gray-500 mt-1">{service.shortDescription}</p>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1.5 w-fit ${typeInfo.color}`}>
                          <i className={typeInfo.icon}></i>
                          {typeInfo.label}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        {service.parentId ? (
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                            {service.parentId.name}
                          </span>
                        ) : (
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-sm font-semibold">
                            Parent
                          </span>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        <button
                          onClick={() => handleToggleActive(service._id)}
                          className={`px-3 py-1 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            service.isActive 
                              ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                              : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                          }`}
                        >
                          {service.isActive ? 'Hoạt động' : 'Tạm dừng'}
                        </button>
                      </td>
                      <td className="py-4 px-6">
                        {service.isFeatured && (
                          <i className="ri-star-fill text-yellow-500 text-xl"></i>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => handleEdit(service)}
                            className="w-8 h-8 flex items-center justify-center text-blue-600 hover:bg-blue-50 rounded-lg transition-colors cursor-pointer"
                          >
                            <i className="ri-edit-line"></i>
                          </button>
                          <button 
                            onClick={() => handleDelete(service._id)}
                            className="w-8 h-8 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
                          >
                            <i className="ri-delete-bin-line"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && !error && filteredServices.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Hiển thị <span className="font-semibold">{startIndex + 1}</span> - <span className="font-semibold">{Math.min(endIndex, filteredServices.length)}</span> trong tổng số <span className="font-semibold">{filteredServices.length}</span> kết quả
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <i className="ri-arrow-left-double-line"></i>
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <i className="ri-arrow-left-s-line"></i>
              </button>
              
              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(page => {
                    // Show first page, last page, current page, and 1 page before/after current
                    return page === 1 || 
                           page === totalPages || 
                           (page >= currentPage - 1 && page <= currentPage + 1);
                  })
                  .map((page, index, array) => {
                    // Add ellipsis if there's a gap
                    const prevPage = array[index - 1];
                    const showEllipsis = prevPage && page - prevPage > 1;
                    
                    return (
                      <div key={page} className="flex items-center gap-1">
                        {showEllipsis && (
                          <span className="px-2 text-gray-500">...</span>
                        )}
                        <button
                          onClick={() => setCurrentPage(page)}
                          className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${
                            currentPage === page
                              ? 'bg-[#2A4B7C] text-white'
                              : 'text-gray-700 hover:bg-gray-50 border border-gray-300'
                          }`}
                        >
                          {page}
                        </button>
                      </div>
                    );
                  })}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <i className="ri-arrow-right-s-line"></i>
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <i className="ri-arrow-right-double-line"></i>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {(isAddModalOpen || isEditModalOpen) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-4xl w-full my-8">
            <div className="p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6 sticky top-0 bg-white pb-4 border-b">
                <h3 className="text-2xl font-bold text-gray-900">
                  {isEditModalOpen ? 'Chỉnh sửa' : 'Thêm mới'}
                </h3>
                <button 
                  onClick={() => {
                    resetForm();
                    setIsAddModalOpen(false);
                    setIsEditModalOpen(false);
                    setSelectedService(null);
                  }}
                  className="w-8 h-8 flex items-center justify-center text-gray-500 hover:text-gray-700 cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              
              <form onSubmit={isEditModalOpen ? handleUpdate : handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Tên */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tên *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => {
                        const newName = e.target.value;
                        setFormData({ 
                          ...formData, 
                          name: newName,
                          // Auto-generate slug nếu đang thêm mới và chưa có slug tùy chỉnh
                          slug: !isEditModalOpen ? generateSlug(newName) : formData.slug
                        });
                        if (validationErrors.name) {
                          setValidationErrors({ ...validationErrors, name: '' });
                        }
                      }}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent ${
                        validationErrors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Nhập tên"
                    />
                    {validationErrors.name && (
                      <p className="mt-1 text-sm text-red-500">{validationErrors.name}</p>
                    )}
                  </div>

                  {/* Slug */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Slug
                    </label>
                    <input
                      type="text"
                      value={formData.slug}
                      onChange={(e) => {
                        setFormData({ ...formData, slug: e.target.value });
                        if (validationErrors.slug) {
                          setValidationErrors({ ...validationErrors, slug: '' });
                        }
                      }}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent ${
                        validationErrors.slug ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="vd: dich-vu-crm"
                    />
                    {validationErrors.slug && (
                      <p className="mt-1 text-sm text-red-500">{validationErrors.slug}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">
                      Để trống để tự động tạo từ tên. Chỉ dùng chữ thường, số và dấu gạch ngang.
                    </p>
                  </div>

                  {/* Loại */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Loại *
                    </label>
                    <select
                      value={formData.type}
                      onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                    >
                      {typeOptions.map((type) => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Icon */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Icon (RemixIcon class) *
                    </label>
                    <div className="flex gap-3">
                      <div className="w-12 h-12 flex items-center justify-center bg-[#2A4B7C] rounded-lg flex-shrink-0">
                        <i className={`${formData.icon} text-xl text-white`}></i>
                      </div>
                      <div className="flex-1">
                        <input
                          type="text"
                          required
                          value={formData.icon}
                          onChange={(e) => {
                            setFormData({ ...formData, icon: e.target.value });
                            if (validationErrors.icon) {
                              setValidationErrors({ ...validationErrors, icon: '' });
                            }
                          }}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent ${
                            validationErrors.icon ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="vd: ri-service-line"
                        />
                      </div>
                    </div>
                    {validationErrors.icon && (
                      <p className="mt-1 text-sm text-red-500">{validationErrors.icon}</p>
                    )}
                    <p className="mt-2 text-sm text-gray-600">
                      <a 
                        href="https://remixicon.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[#2A4B7C] hover:text-[#00c897] font-medium inline-flex items-center gap-1 transition-colors"
                      >
                        <i className="ri-external-link-line"></i>
                        Xem danh sách icon tại remixicon.com
                      </a>
                    </p>
                  </div>

                  {/* Parent Service */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Dịch vụ cha
                    </label>
                    <select
                      value={formData.parentId || ''}
                      onChange={(e) => setFormData({ ...formData, parentId: e.target.value || null })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                    >
                      <option value="">Không có (là dịch vụ cha)</option>
                      {parentServices
                        .filter(p => isEditModalOpen ? p._id !== selectedService?._id : true)
                        .map((parent) => (
                          <option key={parent._id} value={parent._id}>
                            {parent.name}
                          </option>
                        ))}
                    </select>
                  </div>

                  {/* Thứ tự */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Thứ tự hiển thị
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={formData.order}
                      onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                    />
                  </div>

                  {/* Phụ đề (Subtitle) */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phụ đề (cho trang Giải pháp)
                    </label>
                    <input
                      type="text"
                      value={formData.subtitle}
                      onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                      placeholder="vd: Chuyển đổi số cho doanh nghiệp"
                      maxLength={300}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Phụ đề hiển thị trên trang giải pháp (tối đa 300 ký tự)
                    </p>
                  </div>

                  {/* Mô tả ngắn */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mô tả ngắn
                    </label>
                    <textarea
                      rows={3}
                      value={formData.shortDescription}
                      onChange={(e) => {
                        setFormData({ ...formData, shortDescription: e.target.value });
                        if (validationErrors.shortDescription) {
                          setValidationErrors({ ...validationErrors, shortDescription: '' });
                        }
                      }}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent resize-none ${
                        validationErrors.shortDescription ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Mô tả ngắn gọn (hiển thị trong mega menu, danh sách)"
                      maxLength={200}
                    />
                    {validationErrors.shortDescription && (
                      <p className="mt-1 text-sm text-red-500">{validationErrors.shortDescription}</p>
                    )}
                  </div>

                  {/* Mô tả chi tiết */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mô tả chi tiết (cho trang Giải pháp)
                    </label>
                    <textarea
                      rows={4}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent resize-none"
                      placeholder="Mô tả chi tiết đầy đủ về giải pháp/dịch vụ"
                      maxLength={2000}
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Mô tả đầy đủ hiển thị trên trang giải pháp (tối đa 2000 ký tự)
                    </p>
                  </div>

                  {/* Ảnh đại diện (Featured Image) */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Ảnh đại diện
                    </label>
                    
                    {/* Preview Image */}
                    {previewImage && (
                      <div className="mb-4 relative">
                        <img
                          src={previewImage}
                          alt="Preview"
                          className="w-full h-64 object-cover rounded-lg border border-gray-300"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            setPreviewImage('');
                            setSelectedFile(null);
                            setImageError('');
                            // Reset file input
                            const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
                            if (fileInput) {
                              fileInput.value = '';
                            }
                          }}
                          className="absolute top-2 right-2 w-8 h-8 flex items-center justify-center bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        >
                          <i className="ri-close-line"></i>
                        </button>
                      </div>
                    )}

                    {/* File Input */}
                    <div className="flex items-center gap-4">
                      <label className="flex-1 cursor-pointer">
                        <div className={`border-2 border-dashed rounded-lg p-6 text-center hover:border-[#2A4B7C] transition-colors ${
                          imageError ? 'border-red-500' : 'border-gray-300'
                        }`}>
                          <i className="ri-image-add-line text-4xl text-gray-400 mb-2"></i>
                          <p className="text-sm text-gray-600 mb-1">
                            <span className="text-[#2A4B7C] font-semibold">Click để chọn ảnh</span> hoặc kéo thả vào đây
                          </p>
                          <p className="text-xs text-gray-500">
                            PNG, JPG, WEBP (tối đa 5MB)
                          </p>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleFileSelect}
                          className="hidden"
                        />
                      </label>
                    </div>

                    {imageError && (
                      <p className="mt-2 text-sm text-red-500">{imageError}</p>
                    )}

                    <p className="mt-2 text-xs text-gray-500">
                      Ảnh này sẽ hiển thị trên trang giải pháp và mega menu
                    </p>
                  </div>
                </div>

                {/* Tính năng (Features) */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tính năng
                  </label>
                  <div className="space-y-2">
                    {formData.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={feature}
                          onChange={(e) => {
                            const newFeatures = [...formData.features];
                            newFeatures[index] = e.target.value;
                            setFormData({ ...formData, features: newFeatures });
                          }}
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2A4B7C] focus:border-transparent"
                          placeholder={`Tính năng ${index + 1}`}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newFeatures = formData.features.filter((_, i) => i !== index);
                            setFormData({ ...formData, features: newFeatures });
                          }}
                          className="w-10 h-10 flex items-center justify-center text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <i className="ri-delete-bin-line text-xl"></i>
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({ ...formData, features: [...formData.features, ''] });
                      }}
                      className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg text-[#2A4B7C] hover:border-[#2A4B7C] hover:bg-[#2A4B7C]/5 transition-colors font-medium flex items-center justify-center gap-2"
                    >
                      <i className="ri-add-line text-xl"></i>
                      Thêm tính năng
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">
                    Các tính năng nổi bật của giải pháp/dịch vụ (hiển thị trên trang Solutions)
                  </p>
                </div>

                {/* Checkboxes */}
                <div className="flex gap-6">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id={isEditModalOpen ? "isActiveEdit" : "isActive"}
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      className="w-5 h-5 text-[#2A4B7C] border-gray-300 rounded focus:ring-[#2A4B7C] cursor-pointer"
                    />
                    <label htmlFor={isEditModalOpen ? "isActiveEdit" : "isActive"} className="text-sm font-medium text-gray-700 cursor-pointer">
                      Kích hoạt
                    </label>
                  </div>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id={isEditModalOpen ? "isFeaturedEdit" : "isFeatured"}
                      checked={formData.isFeatured}
                      onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
                      className="w-5 h-5 text-[#2A4B7C] border-gray-300 rounded focus:ring-[#2A4B7C] cursor-pointer"
                    />
                    <label htmlFor={isEditModalOpen ? "isFeaturedEdit" : "isFeatured"} className="text-sm font-medium text-gray-700 cursor-pointer">
                      Nổi bật
                    </label>
                  </div>
                </div>
                
                <div className="flex gap-4 pt-4 sticky bottom-0 bg-white border-t mt-6 pt-6">
                  <button
                    type="submit"
                    className="flex-1 bg-[#2A4B7C] hover:bg-[#2A4B7C]/90 text-white py-3 rounded-lg font-semibold transition-colors whitespace-nowrap"
                  >
                    {isEditModalOpen ? 'Cập nhật' : 'Tạo mới'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      resetForm();
                      setIsAddModalOpen(false);
                      setIsEditModalOpen(false);
                      setSelectedService(null);
                    }}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap"
                  >
                    Hủy
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnifiedServicesManagement;

